import { useState, useEffect, memo, useCallback, useRef } from 'react';
import { cn } from '@client/lib/utils';
import { formatRelativeTime } from '@client/lib/utils';
import { useDoubleTap, useLongPress } from '@client/hooks';
import { hapticLight, hapticSuccess, hapticMedium, hapticNudge } from '@client/lib/haptics';
import { Download, Heart, Smile } from 'lucide-react';
import { EmojiPicker, FloatingEmoji, ReactionDisplay } from '@client/components/features/reactions';
import type { PhotoWithUser, ReactionCount } from '@shared/types';

interface PhotoCardProps {
  photo: PhotoWithUser;
  onReact: (photoId: string, emoji: string) => Promise<void>;
  externalLike?: { username: string; emoji?: string }; // Someone who just reacted
  priority?: boolean;
}

function imageFormatFromContentType(contentType: string | null): { extension: string; type: string } {
  switch (contentType) {
    case 'image/png':
      return { extension: 'png', type: 'image/png' };
    case 'image/webp':
      return { extension: 'webp', type: 'image/webp' };
    case 'image/heic':
      return { extension: 'heic', type: 'image/heic' };
    case 'image/heif':
      return { extension: 'heif', type: 'image/heif' };
    default:
      return { extension: 'jpg', type: 'image/jpeg' };
  }
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

export const PhotoCard = memo(function PhotoCard({ photo, onReact, externalLike, priority = false }: PhotoCardProps) {
  const [userReactions, setUserReactions] = useState<string[]>(photo.currentUserReactions || []);
  const [reactions, setReactions] = useState<ReactionCount[]>(photo.reactions || []);
  const [totalCount, setTotalCount] = useState(photo.likeCount);
  const [showFloatingEmoji, setShowFloatingEmoji] = useState<string | null>(null);
  const [showExternalReaction, setShowExternalReaction] = useState(false);
  const [externalReactionData, setExternalReactionData] = useState<{ username: string; emoji: string } | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [isReacting, setIsReacting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [heartBounce, setHeartBounce] = useState(false);
  const imageRef = useRef<HTMLDivElement>(null);

  // Check if user has reacted with a specific emoji
  const hasReaction = useCallback((emoji: string) => userReactions.includes(emoji), [userReactions]);

  // Handle reaction toggle
  const handleReact = useCallback(async (emoji: string) => {
    if (isReacting) return;
    
    hapticSuccess();
    setShowPicker(false);

    // Show floating emoji animation
    setShowFloatingEmoji(emoji);

    // Optimistic update
    const hadReaction = hasReaction(emoji);
    
    if (hadReaction) {
      // Remove this reaction
      setUserReactions(prev => prev.filter(e => e !== emoji));
      setTotalCount(c => Math.max(0, c - 1));
      setReactions(prev => {
        return prev.map(r => 
          r.emoji === emoji ? { ...r, count: Math.max(0, r.count - 1) } : r
        ).filter(r => r.count > 0);
      });
    } else {
      // Add this reaction
      setUserReactions(prev => [...prev, emoji]);
      setTotalCount(c => c + 1);
      setReactions(prev => {
        const existing = prev.find(r => r.emoji === emoji);
        if (existing) {
          return prev.map(r => r.emoji === emoji ? { ...r, count: r.count + 1 } : r);
        } else {
          return [...prev, { emoji, count: 1 }];
        }
      });
    }

    setIsReacting(true);
    try {
      await onReact(photo.id, emoji);
    } catch (error) {
      // Revert on error
      setUserReactions(photo.currentUserReactions || []);
      setReactions(photo.reactions || []);
      setTotalCount(photo.likeCount);
    } finally {
      setIsReacting(false);
    }
  }, [hasReaction, isReacting, onReact, photo.id, photo.likeCount, photo.reactions, photo.currentUserReactions]);

  // Double tap to like with ❤️
  const handleDoubleTapLike = useCallback(async () => {
    await handleReact('❤️');
  }, [handleReact]);

  // Long press to show picker
  const handleLongPress = useCallback(() => {
    hapticMedium();
    setShowPicker(true);
  }, []);

  const { handleTap } = useDoubleTap({
    onDoubleTap: handleDoubleTapLike,
  });

  const longPressHandlers = useLongPress({
    onLongPress: handleLongPress,
    delay: 500,
  });

  // Toggle heart (quick action)
  const handleToggleHeart = useCallback(async () => {
    hapticLight();
    setHeartBounce(true);
    setTimeout(() => setHeartBounce(false), 400);
    await handleReact('❤️');
  }, [handleReact]);

  // Open emoji picker
  const handleOpenPicker = useCallback(() => {
    hapticLight();
    setShowPicker(true);
  }, []);

  const handleDownload = useCallback(async () => {
    if (isDownloading) return;

    hapticLight();
    setIsDownloading(true);

    try {
      const response = await fetch(photo.originalUrl || photo.thumbnailUrl);
      if (!response.ok) {
        throw new Error('Download failed');
      }

      const responseBlob = await response.blob();
      const format = imageFormatFromContentType(response.headers.get('Content-Type') || responseBlob.type);
      const filename = `sabrina-stefan-${photo.id}.${format.extension}`;
      const blob = responseBlob.type ? responseBlob : responseBlob.slice(0, responseBlob.size, format.type);
      const file = new File([blob], filename, { type: blob.type || format.type });
      const shareData: ShareData = {
        files: [file],
        title: 'Sabrina & Stefan',
        text: 'Foto aus der Hochzeitsgalerie',
      };

      if (navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        downloadBlob(blob, filename);
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      console.error('Error sharing photo:', error);
      alert('Teilen fehlgeschlagen. Bitte versuche es nochmal.');
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, photo.id, photo.originalUrl, photo.thumbnailUrl]);

  // Update from external changes (WebSocket updates)
  useEffect(() => {
    setTotalCount(photo.likeCount);
    setUserReactions(photo.currentUserReactions || []);
    setReactions(photo.reactions || []);
  }, [photo.likeCount, photo.currentUserReactions, photo.reactions]);

  // Handle external reaction animation
  useEffect(() => {
    if (externalLike) {
      if (document.visibilityState === 'visible') {
        hapticNudge();
      }

      setExternalReactionData({
        username: externalLike.username,
        emoji: externalLike.emoji || '❤️',
      });
      setShowExternalReaction(true);
      setShowFloatingEmoji(externalLike.emoji || '❤️');
      
      const bannerTimer = setTimeout(() => setShowExternalReaction(false), 2500);
      
      return () => {
        clearTimeout(bannerTimer);
      };
    }
  }, [externalLike]);

  const handleFloatingEmojiComplete = useCallback(() => {
    setShowFloatingEmoji(null);
  }, []);

  const hasHeartReaction = hasReaction('❤️');
  const otherReactions = userReactions.filter(e => e !== '❤️');

  return (
    <article className="bg-card contain-content [content-visibility:auto] [contain-intrinsic-size:640px]">
      {/* Header */}
      <div className="flex items-center px-4 py-3">
        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
          <span className="text-[12px] leading-[16px] font-semibold">
            {photo.username.charAt(0).toUpperCase()}
          </span>
        </div>
        <span className="ml-3 text-[15px] leading-[20px] font-semibold">
          {photo.username}
        </span>
      </div>

      {/* Image */}
      <div
        ref={imageRef}
        className="relative aspect-square bg-secondary cursor-pointer select-none overflow-hidden [touch-action:pan-y]"
        onClick={handleTap}
        {...longPressHandlers}
      >
        <img
          src={photo.thumbnailUrl}
          alt=""
          className="w-full h-full object-cover"
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          sizes="(max-width: 512px) 100vw, 512px"
          draggable={false}
        />

        {/* Floating emoji animation */}
        {showFloatingEmoji && (
          <FloatingEmoji 
            emoji={showFloatingEmoji} 
            onComplete={handleFloatingEmojiComplete}
          />
        )}

        {/* External reaction banner */}
        {showExternalReaction && externalReactionData && (
          <div className="absolute bottom-4 left-0 right-0 flex justify-center pointer-events-none animate-slide-up-fade">
            <div className="bg-black/70 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2">
              <span className="text-xl">{externalReactionData.emoji}</span>
              <span className="text-white text-[14px] font-medium">
                {externalReactionData.username}
              </span>
            </div>
          </div>
        )}

        {/* Emoji picker */}
        {showPicker && (
          <EmojiPicker
            selectedEmojis={userReactions}
            onSelect={handleReact}
            onClose={() => setShowPicker(false)}
          />
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-2">
        <div className="flex items-center gap-2">
          {/* Heart button */}
          <button
            onClick={handleToggleHeart}
            disabled={isReacting}
            className="ios-tap p-1 -ml-1"
          >
            <Heart
              className={cn(
                'w-6 h-6 transition-all duration-200',
                hasHeartReaction 
                  ? 'fill-red-500 text-red-500' 
                  : 'text-foreground',
                heartBounce && 'animate-like-bounce'
              )}
            />
          </button>

          {/* Show user's other reactions as small badges */}
          {otherReactions.length > 0 && (
            <div className="flex -space-x-1">
              {otherReactions.slice(0, 3).map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => handleReact(emoji)}
                  className="w-7 h-7 flex items-center justify-center bg-secondary rounded-full text-base hover:scale-110 transition-transform"
                >
                  {emoji}
                </button>
              ))}
              {otherReactions.length > 3 && (
                <span className="w-7 h-7 flex items-center justify-center bg-secondary rounded-full text-xs font-medium">
                  +{otherReactions.length - 3}
                </span>
              )}
            </div>
          )}

          {/* Emoji picker button */}
          <button
            onClick={handleOpenPicker}
            disabled={isReacting}
            className="ios-tap p-1"
            aria-label="Reaktion hinzufügen"
          >
            <Smile className="w-6 h-6 text-foreground" />
          </button>

          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="ios-tap p-1 disabled:opacity-50"
            aria-label="Foto teilen oder herunterladen"
          >
            <Download className="w-6 h-6 text-foreground" />
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Compact reaction display */}
          {reactions.length > 0 && (
            <ReactionDisplay
              reactions={reactions}
              currentUserReactions={userReactions}
              compact
            />
          )}
        </div>

        {/* Full reaction display (if many reactions) */}
        {reactions.length > 3 && (
          <div className="mt-2">
            <ReactionDisplay
              reactions={reactions}
              currentUserReactions={userReactions}
              onReactionClick={handleReact}
            />
          </div>
        )}

        {totalCount > 0 && reactions.length <= 3 && (
          <p className="text-[15px] leading-[20px] font-semibold mt-1">
            {totalCount === 1 ? 'Gefällt 1 Person' : `Gefällt ${totalCount.toLocaleString()} Personen`}
          </p>
        )}

        <p className="text-[15px] leading-[20px] mt-1">
          <span className="font-semibold">{photo.username}</span>
          {' '}
          <span className="text-muted-foreground">{photo.taskDescription}</span>
        </p>

        <p className="text-[12px] leading-[16px] text-muted-foreground mt-1">
          {formatRelativeTime(photo.createdAt)}
        </p>
      </div>
    </article>
  );
});
